import { Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';
import {
  CheckoutHeaderComponentActions,
  selectSignedInDisplayName,
  selectIsPaymentLinks,
  selectExternalUserUrn,
  selectIsUnauthenticatedPaymentLinksUser,
  selectAllowBuyerLogin,
} from '@arc/unified-payments-frontend/shared-stores';
import { selectCheckoutStep } from '@arc/unified-payments-frontend/payment-footer';
import { CheckoutStep } from '@arc/unified-payments-frontend/shared-payment';
import { selectIsNonValidatingTemplate } from '@arc/unified-payments-frontend/pay-on-account';

@Component({
  selector: 'app-payment-header',
  templateUrl: './payment-header.component.html',
  styleUrls: ['./payment-header.component.scss'],
})
export class PaymentHeaderComponent {
  @Input() logoImageUrl = './assets/layout/images/logo.svg';

  showSignInButton$: Observable<boolean>;
  signedInFullName$: Observable<string | null>;

  constructor(private store: Store) {
    const currentStep$ = this.store.select(selectCheckoutStep);
    const isPaymentLinks$ = this.store.select(selectIsPaymentLinks);
    const isUnauthenticated$ = this.store.select(
      selectIsUnauthenticatedPaymentLinksUser,
    );
    const allowBuyerLogin$ = this.store.select(selectAllowBuyerLogin);
    const externalUserUrn$ = this.store.select(selectExternalUserUrn);
    const nameOnAccount$ = this.store.select(selectSignedInDisplayName);
    const isNonValidatingTemplate$ = this.store.select(
      selectIsNonValidatingTemplate,
    );

    // Show Sign In button while user is unauthenticated (covers: initial state,
    // email entry, and email-sent waiting state) on Step 1 only, and only when
    // the tenant allows buyer login on Payment Links.
    // selectIsUnauthenticatedPaymentLinksUser already gates on isPaymentLinks.
    // NonValidating templates never require sign-in.
    this.showSignInButton$ = combineLatest([
      isUnauthenticated$,
      currentStep$,
      isNonValidatingTemplate$,
      allowBuyerLogin$,
    ]).pipe(
      map(
        ([
          isUnauthenticated,
          currentStep,
          isNonValidatingTemplate,
          allowBuyerLogin,
        ]) =>
          isUnauthenticated &&
          currentStep === CheckoutStep.PaymentDetails &&
          !isNonValidatingTemplate &&
          allowBuyerLogin,
      ),
    );

    // Show "Signed in as XX" only after the user has an externalUserUrn (email verified).
    this.signedInFullName$ = combineLatest([
      isPaymentLinks$,
      externalUserUrn$,
      nameOnAccount$,
    ]).pipe(
      map(([isPaymentLinks, externalUserUrn, fullName]) =>
        isPaymentLinks && externalUserUrn && fullName ? fullName : null,
      ),
    );
  }

  onSignInClicked(): void {
    this.store.dispatch(CheckoutHeaderComponentActions.signInClicked());
  }
}
